import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import ReviewService from '../../services/ReviewService/ReviewService';
import Strings from '../../globals/Strings';
import CommentModel from '../../types/CommentModel';
import Constants, {ModalStates} from '../../globals/Constants';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import UserMessaging from '../../utils/UserMessaging';
import {ControlButton, TscButtonType, Glyphs, TscButtonGlyphPlacement} from '@techsmith/tsc-cloud-style-guide';
import Themeable from '../../types/Themeable';

export default class LikeButton extends React.Component<LikeButtonProps, LikeButtonState> {
   readonly state: LikeButtonState = {isSaving: false};

   constructor(props: LikeButtonProps) {
      super(props);
      autoBind(this);
   }

   userHasLiked(): boolean {
      const {comment, userId} = this.props;
      return comment.likes && comment.likes.some(like => like.userId === userId);
   }

   async handleLikeClick(e: any): Promise<void> {
      if (this.state.isSaving) {
         return;
      }

      const {comment, userId, setModalState} = this.props;
      const userHasLiked = this.userHasLiked();

      e.stopPropagation();

      if (!userId) {
         setModalState(ModalStates.selfIdentify);
         return Promise.resolve();
      }

      this.setState({isSaving: true});

      if (userHasLiked) {
         try {
            await ReviewService.unlikeComment(comment, userId);
         } catch {
            UserMessaging.warn(Strings.errors.failedSaveComment);
         }
      } else {
         try {
            await ReviewService.likeComment(comment, userId);
            GoogleAnalyticsService.trackCommenting(userId, Constants.googleAnalytics.commenting.actions.likeComment);
         } catch {
            UserMessaging.warn(Strings.errors.failedSaveComment);
         }
      }
      this.setState({isSaving: false});
   }

   render() {
      const {comment, themeName} = this.props;
      const {isSaving} = this.state;
      const wrapperClasses = classNames('like-btn-wrapper', {unliked: !this.userHasLiked()});
      let likeButtonAriaLabel = Strings.accessibility.comments.likeCommentButtonAriaLabel;

      if (comment.likes.length > 0) {
         const likeCountAriaLabel = comment.likes.length === 1 ?
                                    Strings.accessibility.comments.likeCountAriaLabelSingular :
                                    Strings.accessibility.comments.likeCountAriaLabelPlural.replace('{number}', comment.likes.length.toString());

         likeButtonAriaLabel += likeCountAriaLabel;
      }

      return (
         <div className={wrapperClasses}>
            <ControlButton onClick={this.handleLikeClick}
                           disabled={isSaving}
                           ariaPressed={this.userHasLiked()}
                           buttonType={this.userHasLiked() ? TscButtonType.primary : TscButtonType.floating}
                           themeName={themeName}
                           label={comment.likes.length.toString()}
                           glyph={<Glyphs.ThumbsUp16x16/>}
                           glyphPlacement={TscButtonGlyphPlacement.left}
                           ariaLabel={likeButtonAriaLabel}
                           testId="comment-like-btn" />
         </div>
      );
   }
}

export interface LikeButtonProps extends Themeable {
   comment: CommentModel;
   userId: string;
   setModalState(modalState: ModalStates): void;
}

interface LikeButtonState {
   isSaving: boolean;
}
